import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import './ParentSection.css';
import SpringHockeyForm from './SpringHockeyForm';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';
import {
  collection,
  addDoc,
  Timestamp,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { youtubeIdFromUrl, localSortDateToday } from './utils/chalkTalk';
import {
  uploadRowMatchesRequiredDoc,
  uploadedDocumentTimestampMs,
  uploadedDocumentStorageRefPath,
} from './utils/parentUploadedDocuments';
import ChalkTalkDrillNotes from './components/ChalkTalkDrillNotes';
import useUpcomingCalendarEvents from './hooks/useUpcomingCalendarEvents';
import TeamScheduleHighlights from './components/TeamScheduleHighlights';
import {
  TEAM_LABELS,
  ROSTER_STATS_CSV_URL,
  playerSlug,
  mergePlayerWithOverride,
  shareRosterPlayerLink,
} from './utils/rosterCatalog';
import useRosterCatalog from './hooks/useRosterCatalog';

const ROSTER_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

const REQUIRED_PARENT_DOCUMENTS = [
  {
    key: 'photo_release',
    label: 'Photo Release Form',
    url: 'https://firebasestorage.googleapis.com/v0/b/gladiators-website.firebasestorage.app/o/Photo%20Release%20Form.pdf?alt=media&token=106192ea-15ba-4489-95d3-08682294a4cd',
  },
  {
    key: 'code_of_conduct',
    label: 'Code of Conduct',
    url: 'https://firebasestorage.googleapis.com/v0/b/gladiators-website.firebasestorage.app/o/code%20of%20conduct.pdf?alt=media&token=054ebc4d-430e-45de-91a9-d9d533c0c464',
  },
  {
    key: 'usa_hockey',
    label: 'USA Hockey',
    reminderOnly: true,
  },
];

function cloneSurveyAnswers(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw).forEach((k) => {
    const v = raw[k];
    out[k] = Array.isArray(v) ? [...v] : v;
  });
  return out;
}

const ParentSection = () => {
  const { allOptions: rosterCatalogOptions, findPlayer } = useRosterCatalog();
  const [activeSection, setActiveSection] = useState('schedule');
  const [displayName, setDisplayName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  /** loading = checking auth + Firestore parent record; ok = show portal; error = verification failed */
  const [accessGate, setAccessGate] = useState('loading');
  const [chalkTalkDrills, setChalkTalkDrills] = useState([]);
  const [loadingChalkTalk, setLoadingChalkTalk] = useState(false);
  const [chalkTalkDrillAcks, setChalkTalkDrillAcks] = useState({});
  const [expenses, setExpenses] = useState([]);

  const [feeItems, setFeeItems] = useState([]);
  const [loadingFees, setLoadingFees] = useState(false);
  const [feesError, setFeesError] = useState('');
  const [uploadingProofForItemId, setUploadingProofForItemId] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ title: '', status: '' });
  const [selectedPortalFormKey, setSelectedPortalFormKey] = useState(REQUIRED_PARENT_DOCUMENTS[0].key);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [loadingUploadedDocs, setLoadingUploadedDocs] = useState(false);
  const [uploadedDocsError, setUploadedDocsError] = useState('');
  const [uploadedDocDownloadUrls, setUploadedDocDownloadUrls] = useState({});
  /** `parents/{uid}` name fields — used to match legacy uploads that only stored uploaderName */
  const [parentFirestoreProfile, setParentFirestoreProfile] = useState({ name: '', displayName: '' });
  const uploadedDocSlicesRef = useRef(new Map());
  const {
    events: upcomingEvents,
    loading: loadingUpcomingEvents,
    error: upcomingEventsError,
    refresh: refreshUpcomingEvents,
  } = useUpcomingCalendarEvents();

  const [parentSurveyList, setParentSurveyList] = useState([]);
  const [loadingParentSurveys, setLoadingParentSurveys] = useState(false);
  const [selectedParentSurveyId, setSelectedParentSurveyId] = useState(null);
  const [parentSurveyAnswers, setParentSurveyAnswers] = useState({});
  const [parentSurveyResponseExists, setParentSurveyResponseExists] = useState(false);
  const [parentSurveyTally, setParentSurveyTally] = useState(null);
  const [parentSurveySubmitting, setParentSurveySubmitting] = useState(false);
  const [parentSurveyError, setParentSurveyError] = useState('');

  const [linkedPlayers, setLinkedPlayers] = useState([]);
  const [rosterChildKey, setRosterChildKey] = useState('');
  const [rosterBio, setRosterBio] = useState('');
  const [rosterFunFact, setRosterFunFact] = useState('');
  const [rosterPhotoFile, setRosterPhotoFile] = useState(null);
  const [rosterPhotoPreview, setRosterPhotoPreview] = useState('');
  const [rosterCurrentPhotoUrl, setRosterCurrentPhotoUrl] = useState('');
  const [rosterOverrideLoading, setRosterOverrideLoading] = useState(false);
  const [rosterSaving, setRosterSaving] = useState(false);
  const [rosterMessage, setRosterMessage] = useState({ type: '', text: '' });
  const [rosterShareMessage, setRosterShareMessage] = useState('');
  const [rosterStats, setRosterStats] = useState({});

  const selectedParentSurvey = useMemo(
    () => parentSurveyList.find((s) => s.id === selectedParentSurveyId) || null,
    [parentSurveyList, selectedParentSurveyId]
  );

  const linkedPlayerOptions = useMemo(() => {
    return linkedPlayers
      .map((lp) => {
        const catalog = rosterCatalogOptions.find(
          (r) => r.teamId === lp.teamId && r.playerName === lp.playerName
        );
        if (!catalog) return null;
        return catalog;
      })
      .filter(Boolean);
  }, [linkedPlayers, rosterCatalogOptions]);

  const selectedLinkedPlayer = useMemo(
    () => linkedPlayerOptions.find((p) => p.key === rosterChildKey) || null,
    [linkedPlayerOptions, rosterChildKey]
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => {
      (async () => {
        if (!user) {
          window.location.href = '/login';
          return;
        }
        setDisplayName(user.displayName || '');
        setUserEmail(user.email || '');
        setParentFirestoreProfile({ name: '', displayName: '' });
        setAccessGate('loading');
        try {
          const snap = await getDoc(doc(db, 'parents', user.uid));
          if (!snap.exists()) {
            await signOut(auth);
            window.location.href = '/login?setup=1';
            return;
          }
          const parentData = snap.data();
          if (parentData.accessEnabled === false) {
            await signOut(auth);
            window.location.href = '/login?revoked=1';
            return;
          }
          setParentFirestoreProfile({
            name: typeof parentData.name === 'string' ? parentData.name.trim() : '',
            displayName:
              typeof parentData.displayName === 'string' ? parentData.displayName.trim() : '',
          });
          const links = Array.isArray(parentData.linkedPlayers)
            ? parentData.linkedPlayers.filter(
                (lp) => lp && typeof lp.teamId === 'string' && typeof lp.playerName === 'string'
              )
            : [];
          setLinkedPlayers(links);
          setAccessGate('ok');
        } catch (err) {
          console.error(err);
          setAccessGate('error');
        }
      })();
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const tsvUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSe8xX-BvtFWfnIqe9BWXqybQL4lReUqxmu6E8F8R1YT0SVhDrIiOHIymoYkRcIdqUshCNFoF-6cqRm/pub?gid=0&single=true&output=tsv';
  
    fetch(tsvUrl)
      .then(res => res.text())
      .then(tsv => {
        const [headerLine, ...lines] = tsv.trim().split('\n');
        const headers = headerLine.split('\t');
  
        const idx = h => headers.indexOf(h);
  
        const data = lines.map(line => {
          const cols = line.split('\t');
          // Ensure cols is padded with empty strings
          while (cols.length < headers.length) {
            cols.push('');
          }
  
          return {
            date: cols[idx('Date')] || '',
            category: cols[idx('Category')] || '',
            expense: cols[idx('Expense')] || '',
            vendor: cols[idx('Vendor')] || '',
            amount: cols[idx('Amount')] || ''
          };
        });
  
        setExpenses(data);
      })
      .catch(console.error);
  }, []);
  

  const loadFeeItems = useCallback(() => {
    setLoadingFees(true);
    setFeesError('');
    return httpsCallable(functions, 'getParentFeeItems')()
      .then(({ data }) => {
        setFeeItems(Array.isArray(data.items) ? data.items : []);
      })
      .catch((err) => {
        console.error(err);
        const code = err?.code || '';
        let msg = err?.message || 'Failed to load team dues';
        if (code === 'functions/not-found' || /not found/i.test(String(msg))) {
          msg =
            'Team dues are temporarily unavailable. If this continues, contact the team administrator.';
        } else if (code === 'functions/internal' || code === 'internal') {
          msg = 'Could not load team dues. Check your connection and open Team Dues again.';
        }
        setFeesError(msg);
      })
      .finally(() => setLoadingFees(false));
  }, []);

  useEffect(() => {
    if (activeSection !== 'payments' || accessGate !== 'ok') return;
    loadFeeItems();
  }, [activeSection, accessGate, loadFeeItems]);

  useEffect(() => {
    if (activeSection !== 'chalktalk' || accessGate !== 'ok') return undefined;
    const user = auth.currentUser;
    if (!user) return undefined;
    setLoadingChalkTalk(true);
    const today = localSortDateToday();
    const drillsQuery = query(
      collection(db, 'chalkTalkDrills'),
      where('sortDate', '>=', today),
      orderBy('sortDate'),
      orderBy('order')
    );
    const unsubDrills = onSnapshot(
      drillsQuery,
      (snap) => {
        const list = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        setChalkTalkDrills(list);
        setLoadingChalkTalk(false);
      },
      (err) => {
        console.error('Chalk Talk drills:', err);
        setLoadingChalkTalk(false);
      }
    );
    const ackCol = collection(db, 'parents', user.uid, 'chalkTalkDrillAcks');
    const unsubAcks = onSnapshot(
      ackCol,
      (snap) => {
        const m = {};
        snap.forEach((d) => {
          m[d.id] = d.data().acknowledgedAt;
        });
        setChalkTalkDrillAcks(m);
      },
      (err) => console.error('Chalk Talk drill acks:', err)
    );
    return () => {
      unsubDrills();
      unsubAcks();
    };
  }, [activeSection, accessGate]);

  useEffect(() => {
    if (activeSection !== 'documents' || accessGate !== 'ok') return undefined;
    const user = auth.currentUser;
    if (!user?.uid) return undefined;
    setLoadingUploadedDocs(true);
    setUploadedDocsError('');

    const baseCol = collection(db, 'uploaded_documents');
    const querySpecs = [{ key: 'uid', q: query(baseCol, where('uploaderUid', '==', user.uid), orderBy('timestamp', 'desc')) }];
    const email = (user.email || '').trim();
    if (email) {
      querySpecs.push({
        key: 'email',
        q: query(baseCol, where('uploaderEmail', '==', email), orderBy('timestamp', 'desc')),
      });
    }
    const nameCandidates = new Set();
    [parentFirestoreProfile.name, parentFirestoreProfile.displayName, (displayName || '').trim(), (user.displayName || '').trim()]
      .filter(Boolean)
      .forEach((n) => nameCandidates.add(n));
    for (const n of nameCandidates) {
      querySpecs.push({
        key: `name:${n}`,
        q: query(baseCol, where('uploaderName', '==', n), orderBy('timestamp', 'desc')),
      });
    }

    uploadedDocSlicesRef.current = new Map();

    const mergeSlicesToState = () => {
      const union = new Map();
      for (const inner of uploadedDocSlicesRef.current.values()) {
        inner.forEach((row, id) => union.set(id, row));
      }
      const rows = [...union.values()]
        .filter((row) => !row.archived)
        .sort((a, b) => uploadedDocumentTimestampMs(b) - uploadedDocumentTimestampMs(a));
      setUploadedDocs(rows);
      setLoadingUploadedDocs(false);
    };

    const unsubs = querySpecs.map(({ key, q }) =>
      onSnapshot(
        q,
        (snap) => {
          const inner = new Map();
          snap.forEach((d) => inner.set(d.id, { id: d.id, ...d.data() }));
          uploadedDocSlicesRef.current.set(key, inner);
          mergeSlicesToState();
        },
        (err) => {
          console.error('Uploaded documents:', key, err);
          setUploadedDocsError(
            'Could not load all of your uploaded documents. If this persists after refresh, contact the team administrator.'
          );
          setLoadingUploadedDocs(false);
        }
      )
    );

    return () => {
      unsubs.forEach((u) => u());
    };
  }, [activeSection, accessGate, parentFirestoreProfile.name, parentFirestoreProfile.displayName, displayName]);

  useEffect(() => {
    if (activeSection !== 'documents' || uploadedDocs.length === 0) return;
    const missingRows = uploadedDocs.filter(
      (row) =>
        row.filename &&
        !Object.prototype.hasOwnProperty.call(uploadedDocDownloadUrls, row.id)
    );
    if (missingRows.length === 0) return;
    let cancelled = false;
    Promise.all(
      missingRows.map(async (row) => {
        try {
          const path = uploadedDocumentStorageRefPath(row);
          if (!path) return [row.id, ''];
          const url = await getDownloadURL(ref(storage, path));
          return [row.id, url];
        } catch (err) {
          console.error('Could not resolve uploaded document URL:', row.id, err);
          return [row.id, ''];
        }
      })
    ).then((pairs) => {
      if (cancelled) return;
      setUploadedDocDownloadUrls((prev) => {
        const next = { ...prev };
        pairs.forEach(([id, url]) => {
          next[id] = url;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [activeSection, uploadedDocs, uploadedDocDownloadUrls]);

  const chalkTalkByDate = useMemo(() => {
    const order = [];
    const map = new Map();
    for (const d of chalkTalkDrills) {
      const key = (d.practiceDate || '—').trim() || '—';
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key).push(d);
    }
    return order.map((k) => {
      const arr = (map.get(k) || []).slice().sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
      return [k, arr];
    });
  }, [chalkTalkDrills]);

  const selectedFormDefinition = useMemo(
    () => REQUIRED_PARENT_DOCUMENTS.find((docItem) => docItem.key === selectedPortalFormKey) || null,
    [selectedPortalFormKey]
  );

  const formatUploadedAt = useCallback((raw) => {
    if (!raw) return '—';
    const dt =
      typeof raw?.toDate === 'function'
        ? raw.toDate()
        : raw?.seconds
          ? new Date(raw.seconds * 1000)
          : new Date(raw);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }, []);

  const requiredDocumentStatus = useMemo(
    () =>
      REQUIRED_PARENT_DOCUMENTS.map((requiredDoc) => {
        const matchingUpload = uploadedDocs.find((row) => uploadRowMatchesRequiredDoc(row, requiredDoc));
        return { requiredDoc, matchingUpload };
      }),
    [uploadedDocs]
  );

  const handleChalkTalkDrillAcknowledge = useCallback(async (drillId) => {
    const user = auth.currentUser;
    if (!user || !drillId) return;
    try {
      await setDoc(
        doc(db, 'parents', user.uid, 'chalkTalkDrillAcks', drillId),
        { acknowledgedAt: Timestamp.now() },
        { merge: true }
      );
    } catch (err) {
      console.error(err);
      alert('Could not save your acknowledgment. Try again.');
    }
  }, []);

  const handleChalkTalkDrillAckUndo = useCallback(async (drillId) => {
    const user = auth.currentUser;
    if (!user || !drillId) return;
    try {
      await deleteDoc(doc(db, 'parents', user.uid, 'chalkTalkDrillAcks', drillId));
    } catch (err) {
      console.error(err);
      alert('Could not undo. Try again.');
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'surveys') setParentSurveyError('');
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'surveys' || accessGate !== 'ok') return undefined;
    setLoadingParentSurveys(true);
    const q = query(
      collection(db, 'parentSurveys'),
      where('active', '==', true),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setParentSurveyList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingParentSurveys(false);
      },
      (err) => {
        console.error('Parent surveys list:', err);
        setParentSurveyList([]);
        setLoadingParentSurveys(false);
      }
    );
    return () => unsub();
  }, [activeSection, accessGate]);

  useEffect(() => {
    if (activeSection !== 'surveys' || accessGate !== 'ok') return;
    if (parentSurveyList.length === 0) {
      setSelectedParentSurveyId(null);
      return;
    }
    if (!selectedParentSurveyId || !parentSurveyList.some((s) => s.id === selectedParentSurveyId)) {
      setSelectedParentSurveyId(parentSurveyList[0].id);
    }
  }, [activeSection, accessGate, parentSurveyList, selectedParentSurveyId]);

  useEffect(() => {
    if (activeSection !== 'surveys' || accessGate !== 'ok' || !selectedParentSurveyId) return undefined;
    const user = auth.currentUser;
    if (!user?.uid) return undefined;
    const respRef = doc(db, 'parentSurveys', selectedParentSurveyId, 'responses', user.uid);
    const unsub = onSnapshot(
      respRef,
      (snap) => {
        if (snap.exists()) {
          setParentSurveyResponseExists(true);
          setParentSurveyAnswers(cloneSurveyAnswers(snap.data().answers || {}));
        } else {
          setParentSurveyResponseExists(false);
          setParentSurveyAnswers({});
        }
      },
      (err) => console.error('Parent survey response:', err)
    );
    return () => unsub();
  }, [activeSection, accessGate, selectedParentSurveyId]);

  useEffect(() => {
    if (activeSection !== 'surveys' || accessGate !== 'ok' || !selectedParentSurveyId) {
      setParentSurveyTally(null);
      return undefined;
    }
    const survey = parentSurveyList.find((s) => s.id === selectedParentSurveyId);
    if (!survey || survey.showLiveResults === false) {
      setParentSurveyTally(null);
      return undefined;
    }
    const tallyRef = doc(db, 'parentSurveys', selectedParentSurveyId, 'resultsTally', 'current');
    const unsub = onSnapshot(
      tallyRef,
      (snap) => {
        setParentSurveyTally(snap.exists() ? snap.data() : null);
      },
      (err) => console.error('Parent survey tally:', err)
    );
    return () => unsub();
  }, [activeSection, accessGate, selectedParentSurveyId, parentSurveyList]);

  const toggleParentSurveyMulti = useCallback((qid, opt, checked) => {
    setParentSurveyAnswers((prev) => {
      const cur = Array.isArray(prev[qid]) ? [...prev[qid]] : [];
      if (checked) {
        if (!cur.includes(opt)) cur.push(opt);
      } else {
        const i = cur.indexOf(opt);
        if (i >= 0) cur.splice(i, 1);
      }
      return { ...prev, [qid]: cur };
    });
  }, []);

  const handleParentSurveySubmit = useCallback(async () => {
    const user = auth.currentUser;
    if (!user?.uid || !selectedParentSurveyId || !selectedParentSurvey) return;
    const questions = Array.isArray(selectedParentSurvey.questions) ? selectedParentSurvey.questions : [];
    const answers = {};
    for (const q of questions) {
      if (!q?.id) continue;
      const v = parentSurveyAnswers[q.id];
      if (q.type === 'single') {
        if (typeof v !== 'string' || !v.trim()) {
          setParentSurveyError('Please answer every question.');
          return;
        }
        answers[q.id] = v.trim();
      } else if (q.type === 'multi') {
        if (!Array.isArray(v) || v.length === 0) {
          setParentSurveyError('Please choose at least one option for each multi-select question.');
          return;
        }
        answers[q.id] = [...v];
      } else if (q.type === 'text') {
        const t = typeof v === 'string' ? v.trim() : '';
        if (!t) {
          setParentSurveyError('Please answer every question.');
          return;
        }
        answers[q.id] = t.slice(0, 2000);
      }
    }
    setParentSurveyError('');
    setParentSurveySubmitting(true);
    try {
      const ref = doc(db, 'parentSurveys', selectedParentSurveyId, 'responses', user.uid);
      if (parentSurveyResponseExists) {
        if (!selectedParentSurvey.allowEdit) {
          setParentSurveyError('You already submitted this survey. The team is not accepting changes.');
          return;
        }
        await updateDoc(ref, {
          answers,
          updatedAt: Timestamp.now(),
        });
      } else {
        await setDoc(ref, {
          answers,
          submittedAt: Timestamp.now(),
        });
      }
    } catch (err) {
      console.error(err);
      setParentSurveyError(err?.message || 'Could not save your answers. Try again.');
    } finally {
      setParentSurveySubmitting(false);
    }
  }, [
    selectedParentSurveyId,
    selectedParentSurvey,
    parentSurveyAnswers,
    parentSurveyResponseExists,
  ]);

  const MAX_FEE_PROOF_BYTES = 10 * 1024 * 1024;

  const handleFeeProofUpload = async (itemId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image (photo or screenshot of your payment).');
      return;
    }
    if (file.size > MAX_FEE_PROOF_BYTES) {
      alert('That file is too large. Maximum size is 10 MB.');
      return;
    }
    const user = auth.currentUser;
    if (!user) return;
    const rawExt = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const ext = /^[a-z0-9]+$/.test(rawExt) ? rawExt.slice(0, 8) : 'jpg';
    const objectPath = `parents/${user.uid}/feePaymentProofs/${itemId}/${Date.now()}.${ext}`;
    setUploadingProofForItemId(itemId);
    try {
      const sref = ref(storage, objectPath);
      await uploadBytes(sref, file, { contentType: file.type || 'image/jpeg' });
      const downloadURL = await getDownloadURL(sref);
      await httpsCallable(functions, 'setParentFeePaymentProof')({ itemId, downloadURL });
      const { data } = await httpsCallable(functions, 'getParentFeeItems')();
      setFeeItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      console.error(err);
      alert(
        'Could not upload your proof. Check your connection, try a smaller image, and make sure storage rules are deployed. If it keeps failing, contact the team administrator.'
      );
    } finally {
      setUploadingProofForItemId(null);
    }
  };

  useEffect(() => {
    if (linkedPlayerOptions.length === 0) {
      setRosterChildKey('');
      return;
    }
    if (!rosterChildKey || !linkedPlayerOptions.some((p) => p.key === rosterChildKey)) {
      setRosterChildKey(linkedPlayerOptions[0].key);
    }
  }, [linkedPlayerOptions, rosterChildKey]);

  useEffect(() => {
    if (activeSection !== 'rosterProfile' || accessGate !== 'ok') return;
    fetch(ROSTER_STATS_CSV_URL)
      .then((res) => res.text())
      .then((csv) => {
        const rows = csv.split('\n');
        const data = {};
        for (let i = 1; i < rows.length; i++) {
          const cols = rows[i].split(',');
          const key = cols[0]?.trim().toLowerCase();
          if (!key) continue;
          data[key] = {
            goals: cols[1] || '0',
            assists: cols[2] || '0',
            points: cols[3] || '0',
            pims: cols[4] || '0',
          };
        }
        setRosterStats(data);
      })
      .catch(console.error);
  }, [activeSection, accessGate]);

  useEffect(() => {
    if (!rosterPhotoFile) {
      setRosterPhotoPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(rosterPhotoFile);
    setRosterPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [rosterPhotoFile]);

  useEffect(() => {
    if (activeSection !== 'rosterProfile' || accessGate !== 'ok' || !selectedLinkedPlayer) {
      return undefined;
    }
    let cancelled = false;
    setRosterOverrideLoading(true);
    setRosterMessage({ type: '', text: '' });
    setRosterPhotoFile(null);
    (async () => {
      try {
        const base = findPlayer(selectedLinkedPlayer.teamId, selectedLinkedPlayer.playerName);
        const overrideRef = doc(db, 'rosterOverrides', selectedLinkedPlayer.key);
        const snap = await getDoc(overrideRef);
        const override = snap.exists() ? snap.data() : null;
        const merged = mergePlayerWithOverride(base, override);
        if (cancelled) return;
        setRosterBio(merged?.bio || '');
        setRosterFunFact(merged?.funFact || '');
        setRosterCurrentPhotoUrl(merged?.photoUrl || '');
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setRosterMessage({ type: 'error', text: 'Could not load roster profile.' });
        }
      } finally {
        if (!cancelled) setRosterOverrideLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSection, accessGate, selectedLinkedPlayer]);

  const handleSaveRosterProfile = async () => {
    const user = auth.currentUser;
    if (!user || !selectedLinkedPlayer) return;
    setRosterMessage({ type: '', text: '' });
    setRosterShareMessage('');

    if (rosterPhotoFile) {
      if (!rosterPhotoFile.type.startsWith('image/')) {
        setRosterMessage({ type: 'error', text: 'Roster photo must be an image (JPEG or PNG).' });
        return;
      }
      if (rosterPhotoFile.size > ROSTER_PHOTO_MAX_BYTES) {
        setRosterMessage({ type: 'error', text: 'Photo must be 10 MB or smaller.' });
        return;
      }
    }

    setRosterSaving(true);
    try {
      let photoUrl = rosterCurrentPhotoUrl || '';
      if (rosterPhotoFile) {
        const slug = playerSlug(selectedLinkedPlayer.playerName);
        const rawExt = (rosterPhotoFile.name.split('.').pop() || 'jpg').toLowerCase();
        const ext = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(rawExt) ? rawExt : 'jpg';
        const path = `rosterPhotos/${user.uid}/${selectedLinkedPlayer.teamId}/${slug}/${Date.now()}.${ext}`;
        const sref = ref(storage, path);
        await uploadBytes(sref, rosterPhotoFile, {
          contentType: rosterPhotoFile.type || 'image/jpeg',
        });
        photoUrl = await getDownloadURL(sref);
      }

      const payload = {
        teamId: selectedLinkedPlayer.teamId,
        playerName: selectedLinkedPlayer.playerName,
        playerSlug: playerSlug(selectedLinkedPlayer.playerName),
        bio: String(rosterBio || '').trim(),
        funFact: String(rosterFunFact || '').trim(),
        updatedAt: Timestamp.now(),
        updatedBy: user.uid,
      };
      if (photoUrl) payload.photoUrl = photoUrl;

      await setDoc(doc(db, 'rosterOverrides', selectedLinkedPlayer.key), payload);
      setRosterCurrentPhotoUrl(photoUrl || rosterCurrentPhotoUrl);
      setRosterPhotoFile(null);
      setRosterMessage({ type: 'success', text: 'Roster profile saved. Changes appear on the public roster page.' });
    } catch (err) {
      console.error(err);
      setRosterMessage({
        type: 'error',
        text: 'Could not save. Check your connection and try again. If it keeps failing, contact the team administrator.',
      });
    } finally {
      setRosterSaving(false);
    }
  };

  const handleShareRosterProfile = async () => {
    if (!selectedLinkedPlayer) return;
    const result = await shareRosterPlayerLink({
      teamId: selectedLinkedPlayer.teamId,
      playerName: selectedLinkedPlayer.playerName,
    });
    if (result.aborted) return;
    if (result.ok && result.method === 'clipboard') {
      setRosterShareMessage('Link copied to clipboard!');
    } else if (!result.ok) {
      setRosterShareMessage('Share not supported on this device. Copy the roster link from your browser.');
    } else {
      setRosterShareMessage('');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = '/login';
  };

  const totalAssessed = feeItems.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const totalPaidAmount = feeItems.filter((row) => row.paid).reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const balanceRemaining = Math.max(0, totalAssessed - totalPaidAmount);
  const paymentInstructions =
    process.env.REACT_APP_PARENT_PAYMENT_INSTRUCTIONS ||
    'Your invoices will arrive via email from hello@mercury.com you are welcome to pay directly through the banking system or you can Venmo @Mickey-Katz-1. You will see an option to upload proof of payment below and you are welcome to include a screenshot of the receipt as proof. As payments are made we will update this section to keep you informed of any balance due. If you have any questions please freel free to reach out to Mickey or Rusty.';

  if (accessGate === 'loading') {
    return (
      <div className="parent-section parent-section-gate">
        <p className="parent-gate-message">Verifying your account…</p>
      </div>
    );
  }

  if (accessGate === 'error') {
    return (
      <div className="parent-section parent-section-gate">
        <p className="parent-gate-message">
          We could not verify your account. Check your connection and refresh the page. If this continues, contact the team administrator.
        </p>
        <button type="button" className="parents-button" onClick={() => window.location.reload()}>
          Retry
        </button>
        <button type="button" className="logout-button parent-gate-logout" onClick={handleLogout}>
          Log Out
        </button>
      </div>
    );
  }

  return (
    <div className="parent-section">
      <header>
        <h1>Welcome{displayName ? `, ${displayName}` : ', Parent'}!</h1>
        <p className="parent-signed-in">Signed in as <strong>{userEmail}</strong></p>
        <div className="parent-welcome-card" role="region" aria-label="Parent portal overview">
          <p>
            This is your team hub: schedule, team dues and payment status, shared expenses, forms, and practice video notes—all in one place.{' '}
            <Link to="/parents-only/chat" className="parent-portal-chat-link">Open Team Discussions</Link> (team discussions and direct messages) from the Parents menu or here.
          </p>
        </div>
        <nav className="parent-nav-row" aria-label="Parent portal sections">
          <button onClick={() => setActiveSection('schedule')} className={`parents-button ${activeSection === 'schedule' ? 'active' : ''}`}>Team Schedule</button>
          <button onClick={() => setActiveSection('payments')} className={`parents-button ${activeSection === 'payments' ? 'active' : ''}`}>Team Dues</button>
          <button onClick={() => setActiveSection('expenses')} className={`parents-button ${activeSection === 'expenses' ? 'active' : ''}`}>Team Expenses</button>
          <button onClick={() => setActiveSection('documents')} className={`parents-button ${activeSection === 'documents' ? 'active' : ''}`}>Important Documents</button>
          <button onClick={() => setActiveSection('chalktalk')} className={`parents-button ${activeSection === 'chalktalk' ? 'active' : ''}`}>Chalk Talk</button>
          <button onClick={() => setActiveSection('springHockey')} className={`parents-button ${activeSection === 'springHockey' ? 'active' : ''}`}>Spring Hockey</button>
          <button onClick={() => setActiveSection('rosterProfile')} className={`parents-button ${activeSection === 'rosterProfile' ? 'active' : ''}`}>Roster Profile</button>
          <button onClick={() => setActiveSection('surveys')} className={`parents-button ${activeSection === 'surveys' ? 'active' : ''}`}>Team surveys</button>
          <button
            type="button"
            className="parents-button"
            onClick={() =>
              window.open(
                'https://soarsportsapparel.com/pages/glass-city-gladiators-uniform-order',
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            Fall Jersey and Bag Store
          </button>
        </nav>
      </header>

      {activeSection === 'schedule' && (
        <section className="section">
          <h2>Team Schedule</h2>
          <p className="section-lead">
            12U and 14U schedules from TeamSnap, updated automatically. Filter by team and event type, or switch between highlights, list, week, and month views.
          </p>
          <TeamScheduleHighlights
            events={upcomingEvents}
            loading={loadingUpcomingEvents}
            error={upcomingEventsError}
            onRetry={refreshUpcomingEvents}
            showFallbackHint
          />
        </section>
      )}

      {activeSection === 'chalktalk' && (
        <section className="section chalk-talk-section">
          <h2>Chalk Talk</h2>
          <p className="section-lead">
            Upcoming practice plans from your coaches. Review each drill with your skater in order, then acknowledge that drill when you’re done. If you tap by mistake, use <strong>Undo review</strong> on that drill.
          </p>
          {loadingChalkTalk && <p className="chalk-talk-loading">Loading practice plans…</p>}
          {!loadingChalkTalk && chalkTalkByDate.length === 0 && (
            <p className="chalk-talk-empty">No upcoming drills posted yet. Check back closer to practice.</p>
          )}
          {!loadingChalkTalk &&
            chalkTalkByDate.map(([practiceDateKey, drillsForDay]) => {
              const reviewedCount = drillsForDay.filter((d) => !!chalkTalkDrillAcks[d.id]).length;
              const totalDrills = drillsForDay.length;
              const formatDrillAck = (at) => {
                if (!at) return null;
                const dt =
                  typeof at.toDate === 'function'
                    ? at.toDate()
                    : at.seconds
                      ? new Date(at.seconds * 1000)
                      : new Date(at);
                return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
              };
              return (
                <div className="chalk-talk-practice-block" key={practiceDateKey}>
                  <div className="chalk-talk-practice-header">
                    <h3 className="chalk-talk-practice-title">Practice — {practiceDateKey}</h3>
                    <div className="chalk-talk-ack-wrap">
                      <p className="chalk-talk-progress" role="status">
                        {reviewedCount} of {totalDrills} drill{totalDrills === 1 ? '' : 's'} reviewed
                      </p>
                    </div>
                  </div>
                  <div className="drill-grid">
                    {drillsForDay.map((drill, drillIndex) => {
                      const videoUrl = drill.videoUrl || drill.link || '';
                      const youtubeId = youtubeIdFromUrl(videoUrl);
                      const drillAckAt = chalkTalkDrillAcks[drill.id];
                      const drillAckLabel = formatDrillAck(drillAckAt);
                      return (
                        <article className="drill-card" key={drill.id}>
                          <h4 className="drill-card-title">
                            <span className="drill-order-badge" title={`Drill ${drillIndex + 1} of ${totalDrills}`}>
                              {drillIndex + 1}
                            </span>
                            {drill.name || 'Drill'}
                          </h4>
                          {youtubeId ? (
                            <div className="video-wrapper">
                              <iframe
                                src={`https://www.youtube.com/embed/${youtubeId}`}
                                title={drill.name || 'Drill video'}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          ) : videoUrl ? (
                            <p className="drill-video-link-wrap">
                              <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="drill-video-link">
                                Open video link
                              </a>
                            </p>
                          ) : null}
                          {drill.imageUrl ? (
                            <div className="drill-image-wrap">
                              <img src={drill.imageUrl} alt="" className="drill-image" />
                            </div>
                          ) : null}
                          {drill.notes ? <ChalkTalkDrillNotes markdown={drill.notes} className="drill-notes" /> : null}
                          <div className="drill-card-ack">
                            {drillAckLabel ? (
                              <div className="chalk-talk-reviewed-row">
                                <p className="chalk-talk-reviewed" role="status">
                                  This drill reviewed {drillAckLabel}
                                </p>
                                <button
                                  type="button"
                                  className="chalk-talk-ack-undo"
                                  onClick={() => handleChalkTalkDrillAckUndo(drill.id)}
                                >
                                  Undo review
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="chalk-talk-ack-button"
                                onClick={() => handleChalkTalkDrillAcknowledge(drill.id)}
                              >
                                We’ve reviewed this drill
                              </button>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </section>
      )}

      {activeSection === 'expenses' && (
        <section className="section">
          <h2>Team Expenses</h2>
          <p>Here's a transparent look at where our team funds are being spent.</p>
          {expenses.length > 0 ? (
            <div className="table-responsive">
              <table className="event-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Expense</th>
                    <th>Vendor</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((exp, i) => (
                    <tr key={i}>
                      <td>{exp.date}</td>
                      <td>{exp.category}</td>
                      <td>{exp.expense}</td>
                      <td>{exp.vendor}</td>
                      <td>{exp.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No expenses to display.</p>
          )}
        </section>
      )}

      {activeSection === 'payments' && (
        <section className="section">
          <h2>Team Dues</h2>
          <p className="section-lead">
            Season charges and payment status are updated by the team treasurer. Remaining balance reflects line items not yet marked paid. You can upload a photo of your receipt or payment confirmation for each line—optional but helpful for the treasurer.
          </p>
          <div className="parent-payment-instructions" role="note">
            <h4>How to pay</h4>
            <p>{paymentInstructions}</p>
          </div>
          {loadingFees && <p>Loading your team dues…</p>}
          {feesError && <p className="error">{feesError}</p>}
          {!loadingFees && !feesError && (
            <>
              <div className="balances parent-fee-balances">
                <div>Total team charges: ${totalAssessed.toFixed(2)}</div>
                <div>Recorded as paid: ${totalPaidAmount.toFixed(2)}</div>
                <div>Remaining: ${balanceRemaining.toFixed(2)}</div>
              </div>
              {feeItems.length === 0 ? (
                <p style={{ color: 'var(--brand-muted)', textAlign: 'center', padding: '1.5rem 0' }}>
                  No fee line items have been added for your account yet. Check with the team administrator if you expect to see charges here.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="event-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Season</th>
                        <th>Amount</th>
                        <th>Due</th>
                        <th>Status</th>
                        <th>Paid date</th>
                        <th>Note</th>
                        <th>Payment proof</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeItems.map((row) => (
                        <tr key={row.id}>
                          <td>{row.description || '—'}</td>
                          <td>{row.seasonLabel || '—'}</td>
                          <td>${(Number(row.amount) || 0).toFixed(2)}</td>
                          <td>{row.dueDate || '—'}</td>
                          <td>
                            {row.paid ? (
                              <span className="parent-fee-status parent-fee-status-paid">Paid</span>
                            ) : (
                              <span className="parent-fee-status parent-fee-status-open">Due</span>
                            )}
                          </td>
                          <td>{row.paid && row.paidAt ? row.paidAt : '—'}</td>
                          <td style={{ whiteSpace: 'normal', maxWidth: '220px' }}>{row.paidNote || '—'}</td>
                          <td className="parent-fee-proof-cell">
                            {row.paymentProofUrl ? (
                              <a
                                href={row.paymentProofUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="parent-fee-proof-link"
                              >
                                View uploaded
                              </a>
                            ) : null}
                            <label
                              className={`parent-fee-proof-upload${uploadingProofForItemId !== null ? ' is-busy' : ''}`}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                disabled={uploadingProofForItemId !== null}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = '';
                                  if (f) handleFeeProofUpload(row.id, f);
                                }}
                              />
                              <span className="parent-fee-proof-upload-label">
                                {uploadingProofForItemId === row.id
                                  ? 'Uploading…'
                                  : row.paymentProofUrl
                                    ? 'Replace photo'
                                    : 'Upload photo'}
                              </span>
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeSection === 'springHockey' && (
        <section className="section spring-hockey-section">
          <SpringHockeyForm displayName={displayName} userEmail={userEmail} />
        </section>
      )}

      {activeSection === 'rosterProfile' && (
        <section className="section roster-profile-section">
          <h2>Roster Profile</h2>
          <p className="section-lead">
            Update your child&apos;s public roster photo, about section, and fun fact. Name, number, position, and
            season stats are managed by the team and cannot be edited here.
          </p>

          {linkedPlayerOptions.length === 0 ? (
            <p className="roster-profile-empty">
              No players are linked to your account yet. Ask a team administrator to link your child under Parent
              Accounts so you can edit their roster profile.
            </p>
          ) : (
            <>
              <label className="roster-profile-field">
                <span className="roster-profile-label">Select your child</span>
                <select
                  className="roster-profile-select"
                  value={rosterChildKey}
                  onChange={(e) => {
                    setRosterChildKey(e.target.value);
                    setRosterMessage({ type: '', text: '' });
                    setRosterShareMessage('');
                  }}
                >
                  {linkedPlayerOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              {rosterOverrideLoading ? (
                <p className="roster-profile-loading">Loading profile…</p>
              ) : selectedLinkedPlayer ? (
                <div className="roster-profile-editor">
                  <div className="roster-profile-meta" aria-label="Player details (read-only)">
                    <p>
                      <strong>{selectedLinkedPlayer.playerName}</strong>
                      {' · '}
                      {TEAM_LABELS[selectedLinkedPlayer.teamId] || selectedLinkedPlayer.teamId}
                      {' · #'}
                      {selectedLinkedPlayer.number}
                      {' · '}
                      {selectedLinkedPlayer.position}
                    </p>
                    <div className="roster-profile-stats-readonly">
                      <span>
                        G {rosterStats[selectedLinkedPlayer.playerName.toLowerCase()]?.goals || '0'}
                      </span>
                      <span>
                        A {rosterStats[selectedLinkedPlayer.playerName.toLowerCase()]?.assists || '0'}
                      </span>
                      <span>
                        P {rosterStats[selectedLinkedPlayer.playerName.toLowerCase()]?.points || '0'}
                      </span>
                      <span>
                        PIM {rosterStats[selectedLinkedPlayer.playerName.toLowerCase()]?.pims || '0'}
                      </span>
                      <span className="roster-profile-stats-note">Stats are read-only</span>
                    </div>
                  </div>

                  <div className="roster-profile-photo-block">
                    <span className="roster-profile-label">Roster photo</span>
                    <p className="roster-profile-hint">
                      Portrait photo, ideally <strong>900×1000 px</strong> (about 0.9:1). Keep the face near the
                      upper center. JPEG or PNG, max 10 MB. The site crops to fit the roster card.
                    </p>
                    <div className="roster-profile-photo-preview-wrap">
                      {(rosterPhotoPreview || rosterCurrentPhotoUrl) && (
                        <img
                          src={rosterPhotoPreview || rosterCurrentPhotoUrl}
                          alt={`${selectedLinkedPlayer.playerName} roster preview`}
                          className="roster-profile-photo-preview"
                        />
                      )}
                    </div>
                    <label className="roster-profile-file-label">
                      <span className="parents-button small">
                        {rosterPhotoFile || rosterCurrentPhotoUrl ? 'Choose new photo' : 'Upload photo'}
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/*"
                        capture="environment"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          setRosterPhotoFile(file);
                          setRosterMessage({ type: '', text: '' });
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>

                  <label className="roster-profile-field">
                    <span className="roster-profile-label">About</span>
                    <textarea
                      className="roster-profile-textarea"
                      rows={5}
                      value={rosterBio}
                      onChange={(e) => setRosterBio(e.target.value)}
                      placeholder="Short bio for the public roster"
                      maxLength={2000}
                    />
                  </label>

                  <label className="roster-profile-field">
                    <span className="roster-profile-label">Fun fact</span>
                    <textarea
                      className="roster-profile-textarea"
                      rows={3}
                      value={rosterFunFact}
                      onChange={(e) => setRosterFunFact(e.target.value)}
                      placeholder="A fun fact for the roster panel"
                      maxLength={1000}
                    />
                  </label>

                  <div className="roster-profile-actions">
                    <button
                      type="button"
                      className="parents-button"
                      disabled={rosterSaving}
                      onClick={handleSaveRosterProfile}
                    >
                      {rosterSaving ? 'Saving…' : 'Save roster profile'}
                    </button>
                    <button
                      type="button"
                      className="parents-button"
                      disabled={rosterSaving}
                      onClick={handleShareRosterProfile}
                    >
                      Share on social
                    </button>
                    <Link
                      to={`/roster?team=${encodeURIComponent(selectedLinkedPlayer.teamId)}&player=${encodeURIComponent(selectedLinkedPlayer.playerName)}`}
                      className="parents-button roster-profile-view-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on roster
                    </Link>
                  </div>

                  {rosterMessage.text ? (
                    <p
                      className={`roster-profile-message ${
                        rosterMessage.type === 'success' ? 'is-success' : 'is-error'
                      }`}
                    >
                      {rosterMessage.text}
                    </p>
                  ) : null}
                  {rosterShareMessage ? (
                    <p className="roster-profile-message is-success">{rosterShareMessage}</p>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </section>
      )}

      {activeSection === 'surveys' && (
        <section className="section parent-surveys-section">
          <h2>Team surveys</h2>
          <p className="section-lead">
            Share preferences with the team staff. When live results are enabled, you will see how the group responded overall.
            Tallies never show names—only counts.
          </p>
          {loadingParentSurveys && <p className="parent-surveys-loading">Loading surveys…</p>}
          {!loadingParentSurveys && parentSurveyList.length === 0 && (
            <p className="parent-surveys-empty">There are no open surveys right now. Check back later.</p>
          )}
          {!loadingParentSurveys && parentSurveyList.length > 0 && selectedParentSurvey && (
            <>
              <div className="parent-survey-picker" role="tablist" aria-label="Choose a survey">
                {parentSurveyList.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={s.id === selectedParentSurveyId}
                    className={`parent-survey-tab ${s.id === selectedParentSurveyId ? 'is-active' : ''}`}
                    onClick={() => setSelectedParentSurveyId(s.id)}
                  >
                    {s.title || 'Survey'}
                  </button>
                ))}
              </div>
              <div className="parent-survey-detail">
                {selectedParentSurvey.description ? (
                  <p className="parent-survey-description">{selectedParentSurvey.description}</p>
                ) : null}
                {parentSurveyResponseExists && !selectedParentSurvey.allowEdit && (
                  <p className="parent-survey-readonly-note" role="status">
                    You submitted this survey. The team is not accepting changes. Contact the team if you need to fix an
                    answer.
                  </p>
                )}
                {parentSurveyResponseExists && selectedParentSurvey.allowEdit && (
                  <p className="parent-survey-editable-note" role="status">
                    You submitted this survey. You can change your answers below, then tap <strong>Update answers</strong>.
                  </p>
                )}
                {parentSurveyError ? (
                  <p className="parent-survey-error" role="alert">
                    {parentSurveyError}
                  </p>
                ) : null}
                <div className="parent-survey-questions">
                  {(Array.isArray(selectedParentSurvey.questions) ? selectedParentSurvey.questions : []).map((q) => {
                    if (!q?.id) return null;
                    const opts = Array.isArray(q.options) ? q.options : [];
                    return (
                      <fieldset key={q.id} className="parent-survey-fieldset">
                        <legend>{q.prompt || 'Question'}</legend>
                        {q.type === 'text' && (
                          <textarea
                            className="parent-survey-text"
                            rows={3}
                            maxLength={2000}
                            value={typeof parentSurveyAnswers[q.id] === 'string' ? parentSurveyAnswers[q.id] : ''}
                            onChange={(e) =>
                              setParentSurveyAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                            }
                            disabled={
                              parentSurveyResponseExists && !selectedParentSurvey.allowEdit
                            }
                          />
                        )}
                        {q.type === 'single' &&
                          opts.map((opt) => (
                            <label key={opt} className="parent-survey-choice">
                              <input
                                type="radio"
                                name={`parent-survey-${q.id}`}
                                value={opt}
                                checked={parentSurveyAnswers[q.id] === opt}
                                onChange={() =>
                                  setParentSurveyAnswers((prev) => ({ ...prev, [q.id]: opt }))
                                }
                                disabled={
                                  parentSurveyResponseExists && !selectedParentSurvey.allowEdit
                                }
                              />
                              <span>{opt}</span>
                            </label>
                          ))}
                        {q.type === 'multi' &&
                          opts.map((opt) => {
                            const set = new Set(Array.isArray(parentSurveyAnswers[q.id]) ? parentSurveyAnswers[q.id] : []);
                            return (
                              <label key={opt} className="parent-survey-choice">
                                <input
                                  type="checkbox"
                                  checked={set.has(opt)}
                                  onChange={(e) => toggleParentSurveyMulti(q.id, opt, e.target.checked)}
                                  disabled={
                                    parentSurveyResponseExists && !selectedParentSurvey.allowEdit
                                  }
                                />
                                <span>{opt}</span>
                              </label>
                            );
                          })}
                      </fieldset>
                    );
                  })}
                </div>
                {(!parentSurveyResponseExists || selectedParentSurvey.allowEdit) && (
                  <button
                    type="button"
                    className="parents-button parent-survey-submit"
                    onClick={handleParentSurveySubmit}
                    disabled={parentSurveySubmitting}
                  >
                    {parentSurveySubmitting
                      ? 'Saving…'
                      : parentSurveyResponseExists
                        ? 'Update answers'
                        : 'Submit answers'}
                  </button>
                )}
                {selectedParentSurvey.showLiveResults !== false && (
                  <div className="parent-survey-results">
                    <h3>Live results</h3>
                    <p className="parent-survey-results-lead">
                      {parentSurveyTally?.responseCount != null
                        ? `${parentSurveyTally.responseCount} famil${parentSurveyTally.responseCount === 1 ? 'y has' : 'ies have'} responded so far.`
                        : 'No totals yet—be the first to respond.'}
                    </p>
                    {(Array.isArray(selectedParentSurvey.questions) ? selectedParentSurvey.questions : []).map((q) => {
                      if (!q?.id) return null;
                      const block = parentSurveyTally?.byQuestion && parentSurveyTally.byQuestion[q.id];
                      if (q.type === 'text') {
                        const n = block?.textResponseCount ?? 0;
                        return (
                          <div key={q.id} className="parent-survey-result-block">
                            <h4>{q.prompt}</h4>
                            <p className="parent-survey-text-count">{n} text response{n === 1 ? '' : 's'}</p>
                            <p className="parent-survey-text-hint">Verbatim answers are not shown here to protect privacy.</p>
                          </div>
                        );
                      }
                      const counts = block?.counts || {};
                      const opts = Array.isArray(q.options) ? q.options : [];
                      const total = opts.reduce((sum, o) => sum + (Number(counts[o]) || 0), 0) + (Number(counts.__other) || 0);
                      return (
                        <div key={q.id} className="parent-survey-result-block">
                          <h4>{q.prompt}</h4>
                          {total === 0 ? (
                            <p className="parent-survey-no-votes">No responses yet.</p>
                          ) : (
                            <ul className="parent-survey-bars">
                              {opts.map((opt) => {
                                const c = Number(counts[opt]) || 0;
                                const pct = total > 0 ? Math.round((c / total) * 100) : 0;
                                return (
                                  <li key={opt} className="parent-survey-bar-row">
                                    <div className="parent-survey-bar-label">
                                      <span>{opt}</span>
                                      <span className="parent-survey-bar-count">
                                        {c} ({pct}%)
                                      </span>
                                    </div>
                                    <div className="parent-survey-bar-track">
                                      <div className="parent-survey-bar-fill" style={{ width: `${pct}%` }} />
                                    </div>
                                  </li>
                                );
                              })}
                              {(counts.__other || 0) > 0 && (
                                <li className="parent-survey-bar-row">
                                  <div className="parent-survey-bar-label">
                                    <span>Other</span>
                                    <span className="parent-survey-bar-count">
                                      {counts.__other} ({Math.round(((counts.__other || 0) / total) * 100)}%)
                                    </span>
                                  </div>
                                  <div className="parent-survey-bar-track">
                                    <div
                                      className="parent-survey-bar-fill parent-survey-bar-fill--muted"
                                      style={{
                                        width: `${total > 0 ? Math.round(((counts.__other || 0) / total) * 100) : 0}%`,
                                      }}
                                    />
                                  </div>
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {activeSection === 'documents' && (
        <section className="section">
          <h2>Important Documents</h2>
          <p className="section-lead">Download team forms and upload signed copies so the staff has everything on file.</p>
          <div className="parent-documents-grid">
            <article className="parent-documents-card">
              <h3>Forms we need on file</h3>
              <ul className="parent-required-doc-list">
                {requiredDocumentStatus.map(({ requiredDoc, matchingUpload }) => (
                  <li key={requiredDoc.key} className="parent-required-doc-item">
                    <div>
                      {requiredDoc.reminderOnly || !requiredDoc.url ? (
                        <span className="parent-required-doc-reminder">{requiredDoc.label}</span>
                      ) : (
                        <a
                          href={requiredDoc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="parent-required-doc-link"
                        >
                          {requiredDoc.label}
                        </a>
                      )}
                    </div>
                    {matchingUpload ? (
                      <span className="parent-doc-status parent-doc-status-received">
                        Received {formatUploadedAt(matchingUpload.timestamp)}
                      </span>
                    ) : (
                      <span className="parent-doc-status parent-doc-status-missing">Not received yet</span>
                    )}
                  </li>
                ))}
              </ul>
            </article>
            <article className="parent-documents-card">
              <h3>Your uploads</h3>
              {loadingUploadedDocs ? <p className="parent-documents-empty">Loading your uploads...</p> : null}
              {uploadedDocsError ? <p className="error">{uploadedDocsError}</p> : null}
              {!loadingUploadedDocs && !uploadedDocsError && uploadedDocs.length === 0 ? (
                <p className="parent-documents-empty">No documents uploaded yet.</p>
              ) : null}
              {!loadingUploadedDocs && !uploadedDocsError && uploadedDocs.length > 0 ? (
                <div className="table-responsive parent-documents-table-wrap">
                  <table className="event-table parent-documents-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>File Name</th>
                        <th>Date Uploaded</th>
                        <th>File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadedDocs.map((row) => {
                        const fileName = row.originalFileName || row.filename || '—';
                        const downloadUrl = uploadedDocDownloadUrls[row.id];
                        return (
                          <tr key={row.id}>
                            <td>{row.title || '—'}</td>
                            <td className="parent-doc-filename">{fileName}</td>
                            <td>{formatUploadedAt(row.timestamp)}</td>
                            <td>
                              {downloadUrl ? (
                                <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                                  View file
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </article>
          </div>
          <div className="parent-document-upload-form">
            <h3>Upload Completed Forms</h3>
            <p style={{ fontSize: '0.9rem' }}>Accepted file types: PDF, DOCX, or image files.</p>

            <input
              type="text"
              placeholder="Your Name"
              value={displayName}
              disabled
              style={{ display: 'block', width: '100%', marginBottom: '10px', padding: '8px' }}
            />

            <label htmlFor="portalFormType" className="parent-doc-upload-label">
              This file is for
            </label>
            <select
              id="portalFormType"
              value={selectedPortalFormKey}
              onChange={(e) => {
                setSelectedPortalFormKey(e.target.value);
                setUploadMessage((prev) => ({ ...prev, status: '' }));
              }}
              className="parent-doc-upload-select"
            >
              {REQUIRED_PARENT_DOCUMENTS.map((docItem) => (
                <option key={docItem.key} value={docItem.key}>
                  {docItem.label}
                </option>
              ))}
              <option value="other">Other document</option>
            </select>

            {selectedPortalFormKey === 'other' ? (
              <input
                type="text"
                placeholder="Title of the Document"
                value={uploadMessage.title || ''}
                onChange={(e) => setUploadMessage((prev) => ({ ...prev, title: e.target.value }))}
                style={{ display: 'block', width: '100%', marginBottom: '10px', padding: '8px' }}
              />
            ) : null}

            <input
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              id="uploadFileInput"
              onChange={(e) => {
                setDocumentFile(e.target.files[0]);
                setUploadMessage((prev) => ({ ...prev, status: '' }));
              }}
              style={{ marginBottom: '10px' }}
            />

            <button
              onClick={async () => {
                const docTitle =
                  selectedPortalFormKey === 'other'
                    ? (uploadMessage.title || '').trim()
                    : selectedFormDefinition?.label || 'Document';
                if (!documentFile || !docTitle) {
                  setUploadMessage((prev) => ({
                    ...prev,
                    status: '❌ Please select a file and enter a title for Other document uploads.',
                  }));
                  return;
                }

                setUploadingDoc(true);

                try {
                  const uid = auth.currentUser?.uid;
                  if (!uid) {
                    setUploadMessage((prev) => ({
                      ...prev,
                      status: '❌ You must be signed in to upload.',
                    }));
                    return;
                  }
                  const cleanName = (displayName || userEmail).replace(/[^a-zA-Z0-9-_]/g, '_');
                  const cleanTitle = docTitle.replace(/[^a-zA-Z0-9-_]/g, '_');
                  const filename = `${Date.now()}-${cleanName}-${cleanTitle}-${documentFile.name}`;
                  const fileRef = ref(storage, `uploads/${uid}/${filename}`);
                  await uploadBytes(fileRef, documentFile);

                  await addDoc(collection(db, 'uploaded_documents'), {
                    uploaderName: displayName || userEmail,
                    uploaderUid: auth.currentUser?.uid || null,
                    uploaderEmail: userEmail || null,
                    title: docTitle,
                    portalFormKey: selectedPortalFormKey,
                    originalFileName: documentFile.name,
                    filename,
                    timestamp: Timestamp.now(),
                  });

                  setDocumentFile(null);
                  document.getElementById('uploadFileInput').value = null;
                  setUploadMessage({ title: '', status: '✅ File uploaded successfully!' });
                } catch (err) {
                  console.error(err);
                  setUploadMessage((prev) => ({ ...prev, status: '❌ Upload failed. Please try again.' }));
                } finally {
                  setUploadingDoc(false);
                }
              }}
              disabled={uploadingDoc}
              className="parents-button"
            >
              {uploadingDoc ? 'Uploading...' : 'Upload File'}
            </button>

            {uploadMessage.status && (
              <p
                style={{
                  marginTop: '10px',
                  color: uploadMessage.status.startsWith('✅') ? 'green' : 'red',
                }}
              >
                {uploadMessage.status}
              </p>
            )}
          </div>


        </section>
      )}

      <footer>
        <button onClick={handleLogout} className="logout-button">Log Out</button>
      </footer>
    </div>
  );
};

export default ParentSection;